import { ClaimRecord } from '@typesApp/ClaimRecord'
import { DateUtil } from '@utils/dateUtil'

const POLLING = {
  maxWaitTime: 30 * 1000,
  pollingInterval: 1000,
} as const

type ControllerService = {
  fetchClaimRecord: (claimRecordId: string) => Promise<ClaimRecord>
}

type ControllerOptions = {
  maxWaitTime?: number
  pollingInterval?: number
}

type PollingResult = {
  iterationCount: number
  status: 'stopped' | 'time-exceeded' | 'ready' | 'error' | 'precheck-failed'
}

type ControllerEventHandlers = {
  isReady: (claimRecord: ClaimRecord) => boolean
  onReady: (claimRecord: ClaimRecord) => void
  onTimeExceeded: (claimRecord: ClaimRecord) => void
  onError: (error: unknown) => void

  /**
   * Optional check to determine if polling should proceed
   */
  preflightCheck?: () => Promise<boolean>
}

export type PollClaimRecordControllerReturn = {
  start: (handlers: ControllerEventHandlers, claimRecordId: string) => Promise<PollingResult>
  stop: () => void
}

export const pollClaimRecordController = (
  service: ControllerService,
  options: ControllerOptions
): PollClaimRecordControllerReturn => {
  const { maxWaitTime = POLLING.maxWaitTime, pollingInterval = POLLING.pollingInterval } = options

  let isStop = false
  let iterationCount = 0
  let timeout: NodeJS.Timeout
  let activeResolve: ((result: PollingResult) => void) | undefined
  let runId = 0

  const settle = (currentRunId: number, result: PollingResult) => {
    if (!activeResolve || currentRunId !== runId) {
      return
    }

    clearTimeout(timeout)
    activeResolve(result)
    activeResolve = undefined
  }

  const start = async (handlers: ControllerEventHandlers, claimRecordId: string): Promise<PollingResult> => {
    isStop = false
    iterationCount = 1
    const startTime = new Date()
    const currentRunId = (runId += 1)

    if (handlers.preflightCheck) {
      const shouldProceed = await handlers.preflightCheck()
      if (!shouldProceed) {
        return { iterationCount: 0, status: 'precheck-failed' }
      }
    }

    return new Promise<PollingResult>(resolve => {
      activeResolve = resolve

      const poll = async () => {
        try {
          const claimRecord = await service.fetchClaimRecord(claimRecordId)

          if (currentRunId !== runId) {
            return
          }

          if (isStop) {
            settle(currentRunId, { iterationCount, status: 'stopped' })
            return
          }

          if (handlers.isReady(claimRecord)) {
            handlers.onReady(claimRecord)
            settle(currentRunId, { iterationCount, status: 'ready' })
          } else if (DateUtil.isMaxTimeExceeded({ startTime, maxTime: maxWaitTime })) {
            handlers.onTimeExceeded(claimRecord)
            settle(currentRunId, { iterationCount, status: 'time-exceeded' })
          } else {
            iterationCount += 1
            timeout = setTimeout(poll, pollingInterval)
          }
        } catch (error) {
          if (currentRunId !== runId || isStop) {
            settle(currentRunId, { iterationCount, status: 'stopped' })
            return
          }

          handlers.onError(error)
          settle(currentRunId, { iterationCount, status: 'error' })
        }
      }

      // start polling
      return poll()
    })
  }

  const stop = () => {
    isStop = true
    settle(runId, { iterationCount, status: 'stopped' })
  }

  return {
    start,
    stop,
  }
}
